import { defineStore } from 'pinia';
import { normalizeFeatureKey } from '@/core/credit/types';
import {
    buildResultAccessScope,
    buildSubjectResultAccessScope,
    getScopeKeyFromResultAccessScope,
    normalizePresetStyleId,
    normalizeScopeType
} from '@/core/credit/resultAccessScope';

const STORAGE_PREFIX = 'unlock_scope';
const ANONYMOUS_SCOPE = 'anonymous';
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const FEATURE_UNLOCK_SYNC_REASON = Object.freeze({
    USER_MISSING: 'unlock_sync_user_missing',
    IMAGE_MISSING: 'unlock_sync_image_missing',
    NETWORK_ERROR: 'unlock_sync_network_error',
    TIMEOUT: 'unlock_sync_timeout',
    INVALID_PAYLOAD: 'unlock_sync_invalid_payload'
});

const EMPTY_UNLOCK_STATE = Object.freeze({
    scopeKey: '',
    scopeType: 'solo',
    subjectId: null,
    originalHashId: '',
    presetStyleId: null,
    imageId: '',
    unlockedFeatures: [],
    updatedAt: null,
    version: null,
    expiresAt: null
});

const toTrimmedString = (value) => String(value || '').trim();

const normalizeUserId = (value) => toTrimmedString(value);

const normalizeImageId = (value) => toTrimmedString(value);

const extractSubjectIdFromScopeKey = (scopeKey) => {
    const normalizedScopeKey = toTrimmedString(scopeKey);
    if (!normalizedScopeKey.toLowerCase().startsWith('subject:')) {
        return null;
    }

    return toTrimmedString(normalizedScopeKey.slice('subject:'.length)) || null;
};

const resolveScopeInput = ({ scope, imageId } = {}) => {
    if (scope && typeof scope === 'object') {
        const normalizedScopeKey = getScopeKeyFromResultAccessScope(scope);
        const normalizedOriginalHashId = toTrimmedString(scope.originalHashId || scope.imageId || imageId);
        const normalizedPresetStyleId = normalizePresetStyleId(scope.presetStyleId);
        const normalizedSubjectId = toTrimmedString(
            scope.subjectId
            || scope.subject_id
            || extractSubjectIdFromScopeKey(normalizedScopeKey)
        );

        if (normalizedSubjectId) {
            return buildSubjectResultAccessScope({
                subjectId: normalizedSubjectId,
                originalHashId: normalizedOriginalHashId,
                presetStyleId: normalizedPresetStyleId
            });
        }

        if (normalizedScopeKey) {
            return {
                scopeType: normalizeScopeType(scope.scopeType, normalizedPresetStyleId),
                originalHashId: normalizedOriginalHashId,
                presetStyleId: normalizedPresetStyleId,
                scopeKey: normalizedScopeKey,
                subjectId: null
            };
        }

        return buildResultAccessScope({
            originalHashId: normalizedOriginalHashId,
            presetStyleId: normalizedPresetStyleId,
            scopeType: scope.scopeType
        });
    }

    const normalizedImageId = normalizeImageId(imageId);
    return buildResultAccessScope({
        originalHashId: normalizedImageId,
        presetStyleId: null,
        scopeType: 'solo'
    });
};

const buildScopedKey = (userId, scopeKey) => {
    const normalizedUserId = normalizeUserId(userId) || ANONYMOUS_SCOPE;
    const normalizedScopeKey = toTrimmedString(scopeKey);
    return `${normalizedUserId}:${normalizedScopeKey}`;
};

const buildStorageKey = (userId, scopeKey) => {
    const normalizedUserId = normalizeUserId(userId);
    const normalizedScopeKey = toTrimmedString(scopeKey);
    if (!normalizedUserId || !normalizedScopeKey) {
        return null;
    }
    return `${STORAGE_PREFIX}:${normalizedUserId}:${normalizedScopeKey}`;
};

const normalizeUpdatedAt = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const raw = toTrimmedString(value);
    if (!raw) {
        return null;
    }
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
        return numeric;
    }
    const timestamp = Date.parse(raw);
    if (Number.isFinite(timestamp)) {
        return timestamp;
    }
    return null;
};

const normalizeVersion = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const raw = toTrimmedString(value);
    return raw || null;
};

const normalizeUnlockedFeatures = (features = []) => {
    if (!Array.isArray(features)) {
        return null;
    }

    const seen = new Set();
    const normalized = [];

    features.forEach((item) => {
        let featureCode = '';
        let subFeatureCode = 'default';
        if (typeof item === 'string') {
            const [rawFeatureCode, rawSubFeatureCode = 'default'] = item.split('::');
            featureCode = toTrimmedString(rawFeatureCode);
            subFeatureCode = toTrimmedString(rawSubFeatureCode) || 'default';
        } else if (item && typeof item === 'object') {
            featureCode = toTrimmedString(item.featureCode);
            subFeatureCode = toTrimmedString(item.subFeatureCode || 'default') || 'default';
        }

        if (!featureCode) {
            return;
        }

        const key = normalizeFeatureKey(featureCode, subFeatureCode);
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        normalized.push({ featureCode, subFeatureCode });
    });

    return normalized;
};

const normalizeUnlockState = (payload = {}, fallbackScope = null) => {
    const fallbackResolvedScope = resolveScopeInput({ scope: fallbackScope });
    const rawScope = {
        scopeKey: payload?.scopeKey || payload?.scope_key || fallbackResolvedScope?.scopeKey || '',
        scopeType: payload?.scopeType || payload?.scope_type || fallbackResolvedScope?.scopeType || 'solo',
        subjectId: payload?.subjectId || payload?.subject_id || fallbackResolvedScope?.subjectId || null,
        originalHashId: payload?.originalHashId || payload?.original_hash_id || payload?.imageId || payload?.image_id || fallbackResolvedScope?.originalHashId || '',
        presetStyleId: payload?.presetStyleId || payload?.preset_style_id || fallbackResolvedScope?.presetStyleId || null
    };

    const scope = resolveScopeInput({ scope: rawScope });
    const unlockedFeatures = normalizeUnlockedFeatures(payload?.unlockedFeatures);
    if (!scope || unlockedFeatures === null) {
        return null;
    }

    return {
        scopeKey: scope.scopeKey,
        scopeType: scope.scopeType,
        subjectId: scope.subjectId || null,
        originalHashId: scope.originalHashId,
        presetStyleId: scope.presetStyleId,
        imageId: scope.originalHashId,
        unlockedFeatures,
        updatedAt: normalizeUpdatedAt(payload?.updatedAt),
        version: normalizeVersion(payload?.version),
        expiresAt: normalizeUpdatedAt(payload?.expiresAt)
    };
};

const toNumberOrDefault = (value, fallbackValue) => {
    const normalized = Number(value);
    return Number.isFinite(normalized) && normalized >= 0 ? normalized : fallbackValue;
};

const resolveExpiresAt = (state, ttlMs) => {
    const normalizedTtlMs = toNumberOrDefault(ttlMs, DEFAULT_TTL_MS);
    if (normalizedTtlMs === 0) {
        return null;
    }
    const updatedAt = normalizeUpdatedAt(state?.updatedAt) || Date.now();
    return updatedAt + normalizedTtlMs;
};

const withExpiry = (state, ttlMs) => ({
    ...state,
    expiresAt: resolveExpiresAt(state, ttlMs)
});

const isStateExpired = (state) => {
    const expiresAt = normalizeUpdatedAt(state?.expiresAt);
    if (expiresAt === null) {
        return false;
    }
    return Date.now() >= expiresAt;
};

const compareVersion = (nextVersion, currentVersion) => {
    if (nextVersion === null || currentVersion === null) {
        return null;
    }

    const nextNumeric = Number(nextVersion);
    const currentNumeric = Number(currentVersion);
    if (Number.isFinite(nextNumeric) && Number.isFinite(currentNumeric)) {
        if (nextNumeric > currentNumeric) return 1;
        if (nextNumeric < currentNumeric) return -1;
        return 0;
    }

    const nextString = toTrimmedString(nextVersion);
    const currentString = toTrimmedString(currentVersion);
    if (!nextString || !currentString) {
        return null;
    }
    if (nextString === currentString) {
        return 0;
    }
    return null;
};

const shouldReplaceState = (currentState, nextState) => {
    if (!currentState) {
        return true;
    }

    const versionCompare = compareVersion(nextState.version, currentState.version);
    if (versionCompare === 1) {
        return true;
    }
    if (versionCompare === -1) {
        return false;
    }

    const nextUpdatedAt = normalizeUpdatedAt(nextState.updatedAt);
    const currentUpdatedAt = normalizeUpdatedAt(currentState.updatedAt);

    if (nextUpdatedAt !== null && currentUpdatedAt !== null) {
        return nextUpdatedAt >= currentUpdatedAt;
    }
    if (nextUpdatedAt !== null) {
        return true;
    }
    if (currentUpdatedAt !== null) {
        return false;
    }

    return true;
};

const isTimeoutError = (error) => {
    const code = toTrimmedString(error?.code).toLowerCase();
    const message = toTrimmedString(error?.message).toLowerCase();
    if (code === 'econnaborted' || code.includes('timeout')) {
        return true;
    }
    return message.includes('timeout');
};

export const useFeatureUnlockStore = defineStore('featureUnlock', {
    state: () => ({
        memoryStateByScope: {},
        syncProvider: null,
        ttlMs: DEFAULT_TTL_MS
    }),

    actions: {
        createEmptyState(scope = null) {
            const normalizedScope = resolveScopeInput({ scope });
            return {
                ...EMPTY_UNLOCK_STATE,
                scopeKey: normalizedScope?.scopeKey || '',
                scopeType: normalizedScope?.scopeType || 'solo',
                subjectId: normalizedScope?.subjectId || null,
                originalHashId: normalizedScope?.originalHashId || '',
                presetStyleId: normalizedScope?.presetStyleId || null,
                imageId: normalizedScope?.originalHashId || ''
            };
        },

        getMemoryState(userId, scopeKey) {
            const scopedKey = buildScopedKey(userId, scopeKey);
            const state = this.memoryStateByScope[scopedKey];
            if (!state) {
                return null;
            }
            if (isStateExpired(state)) {
                this.removeMemoryStateByScope(scopedKey);
                return null;
            }
            return { ...state };
        },

        writeMemoryState(userId, scopeKey, state) {
            const scopedKey = buildScopedKey(userId, scopeKey);
            this.memoryStateByScope = {
                ...this.memoryStateByScope,
                [scopedKey]: { ...state }
            };
            return { ...state };
        },

        removeMemoryStateByScope(scopedKey) {
            if (!scopedKey || !this.memoryStateByScope[scopedKey]) {
                return;
            }
            const nextState = { ...this.memoryStateByScope };
            delete nextState[scopedKey];
            this.memoryStateByScope = nextState;
        },

        readStorageState(userId, scope) {
            const normalizedScope = resolveScopeInput({ scope });
            const storageKey = buildStorageKey(userId, normalizedScope?.scopeKey || '');
            if (!storageKey) {
                return null;
            }
            try {
                const rawValue = localStorage.getItem(storageKey);
                if (!rawValue) {
                    return null;
                }
                const parsed = JSON.parse(rawValue);
                const normalized = normalizeUnlockState(parsed, normalizedScope);
                if (!normalized) {
                    localStorage.removeItem(storageKey);
                    return null;
                }
                if (isStateExpired(normalized)) {
                    localStorage.removeItem(storageKey);
                    return null;
                }
                return normalized;
            } catch (error) {
                console.warn('[feature-unlock-store] readStorageState failed', {
                    storageKey,
                    error: error?.message || error
                });
                return null;
            }
        },

        writeStorageState(userId, scope, state) {
            const normalizedScope = resolveScopeInput({ scope });
            const storageKey = buildStorageKey(userId, normalizedScope?.scopeKey || '');
            if (!storageKey) {
                return;
            }
            try {
                localStorage.setItem(storageKey, JSON.stringify(state));
            } catch (error) {
                console.warn('[feature-unlock-store] writeStorageState failed', {
                    storageKey,
                    error: error?.message || error
                });
            }
        },

        hydrateScopeUnlockState({ userId, scope } = {}) {
            const normalizedScope = resolveScopeInput({ scope });
            if (!normalizedScope?.scopeKey) {
                return {
                    success: false,
                    reasonCode: FEATURE_UNLOCK_SYNC_REASON.IMAGE_MISSING,
                    state: this.createEmptyState()
                };
            }

            const memoryState = this.getMemoryState(userId, normalizedScope.scopeKey);
            if (memoryState) {
                return {
                    success: true,
                    source: 'memory',
                    state: memoryState
                };
            }

            const storageState = this.readStorageState(userId, normalizedScope);
            if (storageState) {
                this.writeMemoryState(userId, normalizedScope.scopeKey, storageState);
                return {
                    success: true,
                    source: 'storage',
                    state: { ...storageState }
                };
            }

            const emptyState = this.createEmptyState(normalizedScope);
            this.writeMemoryState(userId, normalizedScope.scopeKey, emptyState);
            return {
                success: true,
                source: 'empty',
                state: emptyState
            };
        },

        getScopeUnlockState({ userId, scope } = {}) {
            return this.hydrateScopeUnlockState({ userId, scope }).state;
        },

        setScopeUnlockState({ userId, scope, unlockedFeatures, updatedAt = null, version = null } = {}) {
            const normalizedScope = resolveScopeInput({ scope });
            const normalizedState = normalizeUnlockState({
                ...normalizedScope,
                unlockedFeatures,
                updatedAt,
                version
            }, normalizedScope);
            if (!normalizedState) {
                return {
                    success: false,
                    reasonCode: FEATURE_UNLOCK_SYNC_REASON.INVALID_PAYLOAD,
                    state: this.createEmptyState(normalizedScope)
                };
            }

            const currentState = this.getMemoryState(userId, normalizedState.scopeKey)
                || this.readStorageState(userId, normalizedScope);
            const shouldReplace = shouldReplaceState(currentState, normalizedState);
            const nextState = withExpiry(
                shouldReplace ? normalizedState : (currentState || normalizedState),
                this.ttlMs
            );

            this.writeMemoryState(userId, normalizedState.scopeKey, nextState);
            if (normalizeUserId(userId)) {
                this.writeStorageState(userId, normalizedScope, nextState);
            }

            return {
                success: true,
                replaced: shouldReplace,
                state: { ...nextState }
            };
        },

        markUnlockedForScope({ userId, scope, featureCode, subFeatureCode = 'default', updatedAt = Date.now(), version = null } = {}) {
            const normalizedScope = resolveScopeInput({ scope });
            const normalizedFeatureCode = toTrimmedString(featureCode);
            const normalizedSubFeatureCode = toTrimmedString(subFeatureCode) || 'default';

            if (!normalizedScope?.scopeKey || !normalizedFeatureCode) {
                return {
                    success: false,
                    reasonCode: FEATURE_UNLOCK_SYNC_REASON.INVALID_PAYLOAD,
                    state: this.createEmptyState(normalizedScope)
                };
            }

            const currentState = this.getScopeUnlockState({
                userId,
                scope: normalizedScope
            });
            const nextFeatures = normalizeUnlockedFeatures([
                ...(currentState?.unlockedFeatures || []),
                { featureCode: normalizedFeatureCode, subFeatureCode: normalizedSubFeatureCode }
            ]);

            return this.setScopeUnlockState({
                userId,
                scope: normalizedScope,
                unlockedFeatures: nextFeatures || [],
                updatedAt,
                version
            });
        },

        setSyncProvider(provider) {
            this.syncProvider = typeof provider === 'function' ? provider : null;
        },

        setTtlMs(ttlMs) {
            this.ttlMs = toNumberOrDefault(ttlMs, DEFAULT_TTL_MS);
        },

        async syncScopeUnlockState({ userId, scope, syncProvider = null } = {}) {
            const normalizedUserId = normalizeUserId(userId);
            const normalizedScope = resolveScopeInput({ scope });

            if (!normalizedUserId) {
                return {
                    success: false,
                    reasonCode: FEATURE_UNLOCK_SYNC_REASON.USER_MISSING,
                    state: this.createEmptyState(normalizedScope)
                };
            }
            if (!normalizedScope?.scopeKey) {
                return {
                    success: false,
                    reasonCode: FEATURE_UNLOCK_SYNC_REASON.IMAGE_MISSING,
                    state: this.createEmptyState()
                };
            }

            const effectiveSyncProvider = typeof syncProvider === 'function'
                ? syncProvider
                : this.syncProvider;
            if (typeof effectiveSyncProvider !== 'function') {
                return {
                    success: false,
                    reasonCode: FEATURE_UNLOCK_SYNC_REASON.NETWORK_ERROR,
                    state: this.getScopeUnlockState({ userId: normalizedUserId, scope: normalizedScope })
                };
            }

            try {
                const payload = await effectiveSyncProvider({
                    userId: normalizedUserId,
                    subjectId: normalizedScope.subjectId || null,
                    imageId: normalizedScope.originalHashId,
                    originalHashId: normalizedScope.originalHashId,
                    presetStyleId: normalizedScope.presetStyleId,
                    scopeType: normalizedScope.scopeType,
                    scopeKey: normalizedScope.scopeKey
                });
                const candidate = payload?.data || payload;
                const normalizedCandidate = normalizeUnlockState(candidate, normalizedScope);
                if (!normalizedCandidate) {
                    return {
                        success: false,
                        reasonCode: FEATURE_UNLOCK_SYNC_REASON.INVALID_PAYLOAD,
                        state: this.getScopeUnlockState({ userId: normalizedUserId, scope: normalizedScope })
                    };
                }
                const result = this.setScopeUnlockState({
                    userId: normalizedUserId,
                    scope: normalizedCandidate,
                    unlockedFeatures: normalizedCandidate.unlockedFeatures,
                    updatedAt: normalizedCandidate.updatedAt,
                    version: normalizedCandidate.version
                });
                return {
                    success: true,
                    source: 'sync',
                    replaced: result.replaced,
                    state: result.state
                };
            } catch (error) {
                return {
                    success: false,
                    reasonCode: isTimeoutError(error)
                        ? FEATURE_UNLOCK_SYNC_REASON.TIMEOUT
                        : FEATURE_UNLOCK_SYNC_REASON.NETWORK_ERROR,
                    state: this.getScopeUnlockState({ userId: normalizedUserId, scope: normalizedScope }),
                    error
                };
            }
        },

        hydrateImageUnlockState({ userId, imageId } = {}) {
            const scope = resolveScopeInput({ imageId });
            return this.hydrateScopeUnlockState({ userId, scope });
        },

        getImageUnlockState({ userId, imageId } = {}) {
            const scope = resolveScopeInput({ imageId });
            return this.getScopeUnlockState({ userId, scope });
        },

        setImageUnlockState({ userId, imageId, unlockedFeatures, updatedAt = null, version = null } = {}) {
            const scope = resolveScopeInput({ imageId });
            return this.setScopeUnlockState({
                userId,
                scope,
                unlockedFeatures,
                updatedAt,
                version
            });
        },

        markUnlocked({ userId, imageId, featureCode, subFeatureCode = 'default', updatedAt = Date.now(), version = null } = {}) {
            const scope = resolveScopeInput({ imageId });
            return this.markUnlockedForScope({
                userId,
                scope,
                featureCode,
                subFeatureCode,
                updatedAt,
                version
            });
        },

        async syncImageUnlockState({ userId, imageId, syncProvider = null } = {}) {
            const scope = resolveScopeInput({ imageId });
            return this.syncScopeUnlockState({ userId, scope, syncProvider });
        },

        invalidateByUser(userId) {
            const normalizedUserId = normalizeUserId(userId);
            if (!normalizedUserId) {
                return { success: false };
            }

            Object.keys(this.memoryStateByScope).forEach((scopedKey) => {
                if (scopedKey.startsWith(`${normalizedUserId}:`)) {
                    this.removeMemoryStateByScope(scopedKey);
                }
            });

            try {
                const prefix = `${STORAGE_PREFIX}:${normalizedUserId}:`;
                const total = localStorage.length;
                const keysToDelete = [];
                for (let index = 0; index < total; index += 1) {
                    const key = localStorage.key(index);
                    if (key && key.startsWith(prefix)) {
                        keysToDelete.push(key);
                    }
                }
                keysToDelete.forEach((key) => localStorage.removeItem(key));
            } catch (error) {
                console.warn('[feature-unlock-store] invalidateByUser failed', {
                    userId: normalizedUserId,
                    error: error?.message || error
                });
            }

            return { success: true };
        }
    }
});
